import { Shimmer } from '@flexo/atoms';
import styles from './community-stats.module.scss';
import skeletonStyles from '../../.././../atoms/src/lib/skeletons/skeleton.module.scss';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

export interface CommunityStatsSkeletonData {
  totalAmount: string;
  totalAmounttype: string;
  colors: string[];
  keys: string[];
  title: string;
  percentage: number;
}

export interface CommunityStatsSkeletonProps {
  props: any; // The data from the CommunityStats component
  count?: number;
  gradientBorder?: boolean;
  dataInTimeSpan?: boolean;
  isLoading?: boolean;
}

export function CommunityStatsSkeleton({
  props,
  count = 3,
  gradientBorder = false,
  dataInTimeSpan = true,
  isLoading = false,
}: CommunityStatsSkeletonProps) {
  const { t } = useTranslation();
  // FLEXO-6133 #7: mirror the loaded component's web variant — without the
  // __Web paddings the skeleton was ~100px shorter and the page jumped when
  // data arrived.
  const isWeb = (import.meta as any).env?.VITE_APP_IS_WEB === 'true';

  const formatData = () => {
    const output: any[] = [];

    // If props is an array, use it directly
    if (Array.isArray(props)) {
      return props.map((item) => ({
        title: item.title || 'unknown',
      }));
    }

    // If props is an object with data property that's an array
    if (props?.data && Array.isArray(props.data)) {
      return props.data.map((item) => ({
        title: item.title || 'unknown',
      }));
    }

    // If it's an object with properties that aren't data or standard props
    if (props && typeof props === 'object') {
      for (const key in props) {
        if (
          key !== 'dataInTimeSpan' &&
          key !== 'gradientBorder' &&
          key !== 'data'
        ) {
          output.push({ title: key });
        }
      }
    }

    // If no valid props found or count is specified, use count to create placeholders
    if (output.length === 0) {
      const placeholders = ['from_network', 'to_network', 'shared'].slice(
        0,
        count
      );
      placeholders.forEach((title) => {
        output.push({ title });
      });
    }

    return output;
  };

  const statsData = formatData();

  return (
    <div
      className={`${styles.CommunityStats} ${
        isWeb ? styles.CommunityStats__Web : ''
      } ${gradientBorder ? styles.CommunityStats__GradientBorder : ''}`}
      style={{ position: 'relative' }}
    >
      {isLoading && <Shimmer borderRadius={'16px'} />}
      {statsData.map((item) => (
        <div
          className={styles.CommunityStats__Stat}
          key={`${item.title}-${uuidv4()}`}
        >
          {/* Skeleton Circle similar to StatsOverviewSkeleton */}
          <div className={skeletonStyles.StatsOverviewSkeleton__PieContainer}>
            <div
              className={
                skeletonStyles.StatsOverviewSkeleton__PieContainer__Circle
              }
            >
              <div
                className={
                  skeletonStyles.StatsOverviewSkeleton__PieContainer__Circle__Inner
                }
              ></div>
            </div>
          </div>

          {/* Skeleton Text Content */}
          <div className={styles.CommunityStats__Stat__Information}>
            <div className={`heading5 ${styles.CommunityStats__Stat__Title}`}>
              {t(`widgets.communityStats.${item.title}`)}
            </div>
            <div
              className={`${skeletonStyles.StatsOverviewSkeleton__Stats__Percentages__Labels__Value} ${styles.CommunityStats__Stat__Description}`}
            >
              {/* Empty element with skeleton styling */}
            </div>
          </div>
        </div>
      ))}

      {isLoading && <Shimmer borderRadius={'16px'} />}
    </div>
  );
}

export default CommunityStatsSkeleton;
